<script setup lang="ts">

    // 1. Imports
    import { useClickOutside } from '@chatbot/composables/useClickOutside';
    import { getContrastTextColor } from '@chatbot/utils/color';

    // 2. Props
    const props = defineProps<{
        title?: string;
        status?: string;
        avatar?: string;
        isFullscreen?: boolean;
        showFullscreenButton?: boolean;
        primaryColor?: string;
    }>();

    // 3. Emits
    const emit = defineEmits<{
        newConversation: [];
        toggleFullscreen: [];
    }>();

    // 4. Composables
    const { t } = useI18n();

    // 5. Refs / Reactive state
    const isDropdownOpen = ref<boolean>(false);
    const dropdownRef = ref<HTMLElement | null>(null);

    // 6. Computed properties
    const textColor = computed<string>(() => getContrastTextColor(props.primaryColor ?? '#00453a'));

    const statusColor = computed<string>(() => {
        const status = props.status?.toLowerCase() ?? 'online';
        switch (status) {
        case 'offline':
            return 'var(--color-neutral-60)';
        case 'away':
            return 'var(--text-warning)';
        case 'busy':
            return 'var(--text-negative)';
        case 'online':
        default:
            return 'var(--text-success)';
        }
    });

    // 7. Methods / Functions
    const toggleDropdown = (): void => {
        isDropdownOpen.value = !isDropdownOpen.value;
    };

    const closeDropdown = (): void => {
        isDropdownOpen.value = false;
    };

    const handleNewChat = (): void => {
        emit('newConversation');
        isDropdownOpen.value = false;
    };

    // Register click outside listener
    useClickOutside(dropdownRef, () => {
        if (isDropdownOpen.value) {
            closeDropdown();
        }
    });
</script>

<template>
    <div
        class="chat-header"
        :style="{ '--header-text-color': textColor }"
    >
        <div class="header-content">
            <div class="bot-avatar">
                <img
                    v-if="avatar"
                    :src="avatar"
                    :alt="t('chat.botAvatar')"
                    class="bot-avatar-img"
                >
                <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="bot-avatar-icon"
                >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
            </div>
            <div class="header-text">
                <div class="header-title">
                    {{ title || t('chat.title') }}
                </div>
                <span
                    class="status"
                    :style="{ '--status-color': statusColor }"
                >{{ status || t('chat.online') }}</span>
            </div>
            <div class="header-actions">
                <button
                    v-if="showFullscreenButton"
                    type="button"
                    class="action-btn fullscreen-btn"
                    :title="isFullscreen ? t('chat.exitFullscreen') : t('chat.enterFullscreen')"
                    @click="emit('toggleFullscreen')"
                >
                    <svg
                        v-if="!isFullscreen"
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path
                            d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"
                        />
                    </svg>
                    <svg
                        v-else
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path
                            d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"
                        />
                    </svg>
                </button>
                <div
                    ref="dropdownRef"
                    class="dropdown-container"
                >
                    <button
                        type="button"
                        class="action-btn menu-btn"
                        :title="t('chat.menu')"
                        @click="toggleDropdown"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <circle
                                cx="12"
                                cy="12"
                                r="1"
                            />
                            <circle
                                cx="12"
                                cy="5"
                                r="1"
                            />
                            <circle
                                cx="12"
                                cy="19"
                                r="1"
                            />
                        </svg>
                    </button>
                    <div
                        v-if="isDropdownOpen"
                        class="dropdown-menu"
                        @mousedown.stop
                    >
                        <button
                            type="button"
                            class="dropdown-item"
                            @click="handleNewChat"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path d="M12 5v14M5 12h14" />
                            </svg>
                            {{ t('chat.newConversation') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.chat-header {
    padding: calc(var(--spacing-base) * 5);
    background: var(--chat-primary-color);
    box-shadow: 0 2px 10px rgb(0 0 0 / 0.1);
    color: var(--header-text-color);
}

.header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-3x);
}

.bot-avatar {
    display: flex;
    overflow: hidden;
    align-items: center;
    justify-content: center;
    border-radius: var(--border-radius-circle);
    background: rgb(255 255 255 / 0.2);
    block-size: var(--size-6);
    font-size: var(--font-xlarge);
    inline-size: var(--size-6);
}

.bot-avatar-img {
    display: block;
    block-size: 100%;
    inline-size: 100%;
    object-fit: cover;
}

.bot-avatar-icon {
    block-size: 18px;
    color: var(--header-text-color);
    inline-size: 18px;
}

.header-title{
    margin: 0;
    color: var(--header-text-color);
    font-size: var(--font-medium);
    font-weight: var(--font-weight-semi-bold);
}

.status {
    display: flex;
    align-items: center;
    color: var(--header-text-color);
    font-size: var(--font-small);
    gap: var(--spacing-01);
    opacity: 0.9;
}

.status::before {
    display: inline-block;
    border-radius: var(--border-radius-circle);
    background: var(--status-color, var(--text-success));
    block-size: var(--size-0);
    content: '';
    inline-size: var(--size-0);
}

.header-actions {
    display: flex;
    gap: var(--spacing-02);
    margin-inline-start: auto;
}

.action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: var(--border-radius-lg);
    background: color-mix(in srgb, var(--header-text-color) 20%, transparent);
    block-size: var(--size-6);
    color: var(--header-text-color);
    cursor: pointer;
    inline-size: var(--size-6);
    transition: all 0.2s ease;
}

.action-btn:hover {
    background: color-mix(in srgb, var(--header-text-color) 30%, transparent);
    transform: translateY(-2px);
}

.action-btn:active {
    transform: translateY(0);
}

.action-btn svg {
    block-size: 20px;
    inline-size: 20px;
}

.dropdown-container {
    position: relative;
}

.dropdown-menu {
    position: absolute;
    z-index: 1000;
    overflow: hidden;
    border-radius: var(--border-radius-lg);
    animation: slide-down 0.2s ease;
    background: var(--background-secondary-highlight);
    box-shadow: 0 4px 12px rgb(0 0 0 / 0.15);
    inset-block-start: calc(100% + var(--spacing-02));
    inset-inline-end: 0;
    min-inline-size: 180px;
}

@keyframes slide-down {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.dropdown-item {
    display: flex;
    align-items: center;
    border: none;
    background: var(--background-secondary-highlight);
    color: var(--text-secondary);
    cursor: pointer;
    font-size: var(--font-regular);
    gap: var(--spacing-02);
    inline-size: 100%;
    padding-block: var(--spacing-3x);
    padding-inline: var(--spacing-03);
    text-align: start;
    transition: background 0.2s ease;
}

.dropdown-item:hover {
    background: var(--background-primary-highlight);
}

.dropdown-item svg {
    block-size: 16px;
    color: var(--chat-primary-color);
    inline-size: 16px;
}
</style>
